import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../hooks/redux';
import {
  useContentMasterEntitlements,
  useMarketplaceListingAddonActive,
} from '../../hooks/useContentMasterEntitlements';
import { helper } from '../../utils/helper';
import { addNotification } from '../../store/slices/notificationSlice';
import { setContentMasterSubscriptionInsufficientModalVisible } from '../../store/slices/contentMasterSlice';
import ContentMasterProBadge from '../content-master/ContentMasterProBadge';
import { ContentMasterProductView } from '../../api/generated/models/ContentMasterProductView';
import contentMasterIcon from '../../assets/icons/content-master.svg';
import chevronDownIcon from '../../assets/icons/chevron-down.svg';
import chevronUpIcon from '../../assets/icons/chevron-up.svg';
import productIcon from '../../assets/icons/product.svg';
import checkSquareIcon from '../../assets/icons/check-square.svg';
import shieldIcon from '../../assets/icons/shield.svg';

const CONTENT_MASTER_PLUGIN = 'content_master';
const BUY_PATH = '/licenses/buy?plugin=content_master';
/** Buy page deep-link that scrolls to the Add-ons section. */
const BUY_ADDON_PATH = '/licenses/buy?plugin=content_master&scroll=addons';

interface ChildDescriptor {
  labelKey: string;
  to: string;
  icon: string;
  isActive: (pathname: string) => boolean;
  counter?: number | null;
  /** Starter plan: item visible but not navigable - shows Pro badge. */
  proLocked?: boolean;
  /**
   * Marketplace-listing add-on not active: item visible but not navigable -
   * shows Add-on badge; click routes to the buy page's Add-ons section.
   */
  addonLocked?: boolean;
}

interface ContentMasterMenuSectionProps {
  onNavigate?: () => void;
}

const ContentMasterMenuSection: React.FC<ContentMasterMenuSectionProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const subscriptions = useAppSelector((state) => state.licenses.items);
  const parentUserId = useAppSelector((state) => state.profile.contact?.parentUserId);
  const menuCounts = useAppSelector((state) => state.contentMaster.menuCounts);
  const { bulkGenerationIncluded } = useContentMasterEntitlements();
  const marketplaceAddonActive = useMarketplaceListingAddonActive();

  const hasActiveSubscription = subscriptions.some(
    (s) => s.pluginName === CONTENT_MASTER_PLUGIN && helper.isSubscriptionActive(s)
  );

  const productsPath = '/features/content-master';
  const bulkReviewPath = '/features/content-master/bulk-review';
  const listingPath = '/features/content-master/marketplace-listing';
  const usagePath = '/features/content-master/usage';

  const isProductsActive = (pathname: string) =>
    pathname === productsPath || pathname.startsWith('/features/content-master/product/');
  const isBulkReviewActive = (pathname: string) => pathname === bulkReviewPath;
  const isListingActive = (pathname: string) => pathname === listingPath;
  const isUsageActive = (pathname: string) => pathname === usagePath;

  const isAnyChildActive =
    isProductsActive(location.pathname) ||
    isBulkReviewActive(location.pathname) ||
    isListingActive(location.pathname) ||
    isUsageActive(location.pathname);

  const [isExpanded, setIsExpanded] = useState<boolean>(isAnyChildActive);

  useEffect(() => {
    if (isAnyChildActive) {
      setIsExpanded(true);
    }
  }, [isAnyChildActive]);

  const inProgressCount =
    menuCounts?.[ContentMasterProductView.processingStatus.IN_PROGRESS] ?? null;

  /**
   * Bulk Review badge counts every product currently in any bulk_* status -
   * not just active ones - so the user can see at a glance whether they have
   * batches to review (queued / running / done / failed). When the sidebar
   * counts have not been fetched yet (e.g. before the workspace-config call
   * lands) we show no badge.
   */
  const bulkReviewCount = useMemo<number | null>(() => {
    if (!menuCounts) {
      return null;
    }
    let total = 0;
    let anyKnown = false;
    for (const status of [
      ContentMasterProductView.processingStatus.BULK_GENERATE_QUEUED,
      ContentMasterProductView.processingStatus.BULK_GENERATE_IN_PROGRESS,
      ContentMasterProductView.processingStatus.BULK_GENERATE_DONE,
      ContentMasterProductView.processingStatus.BULK_GENERATE_FAILED,
      ContentMasterProductView.processingStatus.BULK_APPLY_QUEUED,
      ContentMasterProductView.processingStatus.BULK_APPLY_IN_PROGRESS,
      ContentMasterProductView.processingStatus.BULK_APPLY_FAILED,
    ]) {
      const v = menuCounts[status];
      if (typeof v === 'number') {
        anyKnown = true;
        total += v;
      }
    }
    if (!anyKnown) {
      return null;
    }
    return total;
  }, [menuCounts]);

  const children: ChildDescriptor[] = [
    {
      labelKey: 'sidebar_content_master_products',
      to: productsPath,
      icon: productIcon,
      isActive: isProductsActive,
      counter: inProgressCount,
    },
    {
      labelKey: 'sidebar_content_master_bulk_review',
      to: bulkReviewPath,
      icon: checkSquareIcon,
      isActive: isBulkReviewActive,
      counter: bulkReviewCount,
      proLocked: !bulkGenerationIncluded,
    },
    {
      labelKey: 'sidebar_content_master_marketplace_listing',
      to: listingPath,
      icon: checkSquareIcon,
      isActive: isListingActive,
      addonLocked: !marketplaceAddonActive,
    },
    {
      labelKey: 'sidebar_content_master_account_tokens',
      to: usagePath,
      icon: shieldIcon,
      isActive: isUsageActive,
    },
  ];

  const goToBuyOrWarn = (path: string = BUY_PATH) => {
    if (parentUserId) {
      dispatch(
        addNotification({
          type: 'warning',
          title: t('notification_warning'),
          message: t('notification_only_parent_user_can_buy_license'),
        })
      );
      return;
    }
    navigate(path);
  };

  const handleChildClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    child: ChildDescriptor
  ) => {
    if (child.proLocked) {
      event.preventDefault();
      dispatch(setContentMasterSubscriptionInsufficientModalVisible(true));
      return;
    }
    if (child.addonLocked) {
      event.preventDefault();
      goToBuyOrWarn(BUY_ADDON_PATH);
      return;
    }
    if (hasActiveSubscription) {
      return;
    }
    event.preventDefault();
    goToBuyOrWarn();
  };

  const toggleExpanded = () => setIsExpanded((prev) => !prev);
  const sectionLabel = t('sidebar_content_master');

  return (
    <li className={`nav-item nav-item-group${isAnyChildActive ? ' active' : ''}`}>
      <div className="nav-item-row">
        <button type="button" className="nav-item-primary" onClick={toggleExpanded}>
          <span className="nav-item-icon">
            <img src={contentMasterIcon} alt="" aria-hidden="true" />
          </span>
          <span className="menu-item-label">{sectionLabel}</span>
        </button>
        <button
          type="button"
          className="nav-item-chevron-btn"
          onClick={toggleExpanded}
          aria-expanded={isExpanded}
          aria-label={
            isExpanded
              ? t('sidebar_collapse_section', { section: sectionLabel })
              : t('sidebar_expand_section', { section: sectionLabel })
          }
        >
          <img src={isExpanded ? chevronUpIcon : chevronDownIcon} alt="" aria-hidden="true" />
        </button>
      </div>
      {isExpanded && (
        <ul className="nav-submenu">
          {children.map((child) => {
            const active = child.isActive(location.pathname);
            const showCounter = typeof child.counter === 'number' && child.counter > 0;
            const locked = Boolean(child.proLocked || child.addonLocked);
            return (
              <li
                key={child.to}
                className={`nav-sub-item${active ? ' active' : ''}${
                  locked ? ' nav-sub-item--pro-locked' : ''
                }`}
              >
                <Link
                  to={
                    hasActiveSubscription && !locked
                      ? child.to
                      : child.addonLocked
                      ? BUY_ADDON_PATH
                      : BUY_PATH
                  }
                  onClick={(event) => {
                    handleChildClick(event, child);
                    if (!event.defaultPrevented) {
                      onNavigate?.();
                    }
                  }}
                  aria-disabled={locked ? true : undefined}
                  className="nav-sub-item-link"
                >
                  <span className="nav-sub-item-row">
                    <span className="nav-sub-item-icon">
                      <img src={child.icon} alt="" aria-hidden="true" />
                    </span>
                    <span className="menu-item-label">{t(child.labelKey)}</span>
                    {locked ? (
                      <ContentMasterProBadge
                        className="nav-sub-item-pro-badge"
                        label={child.addonLocked ? 'Add-on' : undefined}
                        // The whole link already routes to the add-on purchase
                        // - a non-interactive badge avoids opening the Pro
                        // upgrade modal on top of that.
                        interactive={!child.addonLocked}
                        title={
                          child.addonLocked
                            ? t('cm_addon_marketplace_menu_locked')
                            : t('cm_entitlements_starter_bulk_disabled')
                        }
                      />
                    ) : null}
                    {showCounter && !locked ? (
                      <span className="nav-sub-item-badge">{child.counter}</span>
                    ) : null}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
};

export default ContentMasterMenuSection;
